import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Truck, Printer, PackageCheck, RefreshCw, XCircle, Search, Undo2 } from '@/components/icons';
import { useNavigate } from 'react-router-dom';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Input } from '@/components/form/Input';
import { Table } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { formatINR } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import {
  listShippingRows, bookShipment, requestPickup, trackShipment, cancelShipment, setShipmentCod,
  getNdrQueue, type ShippingRow, type TrackScan, type Shipment,
} from '@/api/delhivery';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { NdrQueue, NDR_QUERY_KEY } from './NdrQueue';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'muted'> = {
  Delivered: 'success', InTransit: 'info', OutForDelivery: 'warning', Booked: 'info',
  Manifested: 'muted', PickedUp: 'info', PickupRequested: 'info',
  NDR: 'warning', RTO: 'danger', Cancelled: 'muted', Failed: 'danger',
};
const STATUS_LABEL: Record<string, string> = {
  OutForDelivery: 'Out for Delivery', PickedUp: 'Picked Up', PickupRequested: 'Pickup Requested', InTransit: 'In Transit',
};
// Tracking timeline — the courier-panel view: the journey as stages, each with the
// date it was reached and its latest scan, branching to the return leg on RTO.
type StageKey = 'manifested' | 'pickupScheduled' | 'pickedUp' | 'transit' | 'ofd' | 'delivered' | 'returning' | 'returned' | 'cancelled';
const FORWARD: { key: StageKey; label: string }[] = [
  { key: 'manifested', label: 'Order manifested' },
  { key: 'pickupScheduled', label: 'Pickup scheduled' },
  { key: 'pickedUp', label: 'Picked up' },
  { key: 'transit', label: 'In transit' },
  { key: 'ofd', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
];
// Where the shipment's own status places it when the scans don't say more.
const STATUS_STAGE: Record<string, StageKey> = {
  Booked: 'manifested', Manifested: 'manifested', PickupRequested: 'pickupScheduled', PickedUp: 'pickedUp',
  InTransit: 'transit', NDR: 'ofd', OutForDelivery: 'ofd', Delivered: 'delivered', RTO: 'returning', Cancelled: 'cancelled',
};

/** Which stage a single Delhivery scan belongs to (null = informational only, e.g. "Not Picked"). */
function scanStage(sc: TrackScan): StageKey | null {
  const t = `${sc.status} ${sc.note}`.toLowerCase();
  const type = (sc.type ?? '').toUpperCase();
  if (type === 'RT' || /\brto\b|return/.test(t)) {
    return type === 'DL' || /returned to (origin|shipper|seller)|rto delivered|delivered to (origin|shipper|seller)/.test(t) ? 'returned' : 'returning';
  }
  if (t.includes('cancel')) return 'cancelled';
  if (type === 'DL' || (t.includes('delivered') && !/undelivered|not delivered/.test(t))) return 'delivered';
  if (/not picked|pickup/.test(t) && !/picked up|pickup (done|completed)/.test(t)) return null;
  // A failed delivery attempt (NDR) belongs with the delivery run it came from.
  if (/dispatched|out for delivery|undelivered|not delivered|refused|attempt|consignee/.test(t)) return 'ofd';
  if (/picked up|pickup (done|completed)/.test(t)) return 'pickedUp';
  if (t.includes('manifest')) return 'manifested';
  return 'transit';
}

const fmtWhen = (at: string | null | undefined) =>
  !at ? ''
    // A bare date (e.g. the pickup date) has no time — don't invent one.
    : /^\d{4}-\d{2}-\d{2}$/.test(at) ? new Date(`${at}T00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : new Date(at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const scanText = (sc: TrackScan) => sc.note || sc.status;

type TrackData = Shipment & { scans: TrackScan[] };
type TimelineRow = { key: StageKey; label: string; state: 'done' | 'current' | 'upcoming'; tone: 'ok' | 'bad'; at: string | null; detail: TrackScan | null };

function TrackingTimeline({ data }: { data: TrackData }) {
  const [showScans, setShowScans] = useState(false);
  // Oldest first — scans arrive newest first.
  const chrono = [...data.scans].sort((a, b) => (a.at ? Date.parse(a.at) : 0) - (b.at ? Date.parse(b.at) : 0));
  const first: Partial<Record<StageKey, TrackScan>> = {};
  const last: Partial<Record<StageKey, TrackScan>> = {};
  for (const sc of chrono) {
    const k = scanStage(sc);
    if (!k) continue;
    first[k] ??= sc;
    last[k] = sc;
  }
  const fwdIdx = (k: StageKey) => FORWARD.findIndex((s) => s.key === k);
  const statusStage = STATUS_STAGE[data.status];
  const branch: 'returning' | 'cancelled' | null =
    first.returned || first.returning || statusStage === 'returning' ? 'returning'
      : first.cancelled || statusStage === 'cancelled' ? 'cancelled' : null;
  // Furthest forward stage reached — from scans, the pickup request and the stored status.
  let reach = Math.max(0, ...FORWARD.map((s, i) => (first[s.key] ? i : -1)));
  if (data.pickupId) reach = Math.max(reach, fwdIdx('pickupScheduled'));
  if (statusStage && fwdIdx(statusStage) >= 0) reach = Math.max(reach, fwdIdx(statusStage));
  const delivered = !branch && reach === fwdIdx('delivered');

  const rows: TimelineRow[] = [];
  // On a branch (RTO / cancelled) only the forward stages actually reached are shown.
  FORWARD.slice(0, branch ? reach + 1 : FORWARD.length).forEach((s, i) => {
    rows.push({
      key: s.key, label: s.label, tone: 'ok',
      state: branch || delivered || i < reach ? 'done' : i === reach ? 'current' : 'upcoming',
      at: first[s.key]?.at ?? (s.key === 'pickupScheduled' ? data.pickupDate?.slice(0, 10) ?? null : null),
      detail: last[s.key] ?? null,
    });
  });
  if (branch === 'returning') {
    const back = !!first.returned;
    rows.push({ key: 'returning', label: 'Returning to origin', tone: 'bad', state: back ? 'done' : 'current', at: first.returning?.at ?? null, detail: last.returning ?? null });
    rows.push({ key: 'returned', label: 'Returned to origin', tone: 'bad', state: back ? 'current' : 'upcoming', at: first.returned?.at ?? null, detail: last.returned ?? null });
  } else if (branch === 'cancelled') {
    rows.push({ key: 'cancelled', label: 'Cancelled', tone: 'bad', state: 'current', at: first.cancelled?.at ?? null, detail: last.cancelled ?? null });
  }
  const latest = chrono[chrono.length - 1];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--border-color)] px-3 py-2">
        <Badge tone={STATUS_TONE[data.status] ?? 'muted'}>{STATUS_LABEL[data.status] ?? data.status}</Badge>
        <span className="text-[11px] text-[var(--text-muted)]">
          {latest ? `Last update ${fmtWhen(latest.at)}${latest.location ? ` · ${latest.location}` : ''}` : 'No courier scans yet'}
        </span>
      </div>
      {data.status === 'NDR' && (
        <div className="mb-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[12px] font-semibold text-warning">
          Delivery attempt failed (NDR){data.statusDetail ? ` — ${data.statusDetail}` : ''}
        </div>
      )}
      <ol className="flex flex-col">
        {rows.map((r, i) => {
          const next = rows[i + 1];
          const dot = r.state === 'upcoming'
            ? 'border-2 border-[var(--border-color)]'
            : r.tone === 'bad' ? 'bg-danger' : 'bg-success';
          const ring = r.state === 'current' ? (r.tone === 'bad' ? 'ring-4 ring-danger/20' : 'ring-4 ring-success/20') : '';
          const line = !next || next.state === 'upcoming' ? 'bg-[var(--border-color)]' : next.tone === 'bad' ? 'bg-danger/60' : 'bg-success/60';
          return (
            <li key={r.key} className="flex gap-3">
              <div className="flex w-3 flex-col items-center">
                <span className={`mt-1 h-3 w-3 shrink-0 rounded-full ${dot} ${ring}`} />
                {next && <span className={`my-0.5 w-0.5 flex-1 ${line}`} />}
              </div>
              <div className={`min-w-0 flex-1 ${next ? 'pb-4' : ''}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className={`text-[13px] font-bold ${r.state === 'upcoming' ? 'text-[var(--text-muted)]' : r.tone === 'bad' ? 'text-danger' : 'text-[var(--text-primary)]'}`}>
                    {r.label}
                  </span>
                  {r.at && <span className="text-[11px] tabular-nums text-[var(--text-muted)]">{fmtWhen(r.at)}</span>}
                </div>
                {r.detail && r.state !== 'upcoming' && (
                  <div className={`mt-0.5 text-[11px] ${r.state === 'current' ? 'text-[var(--text-secondary)]' : 'text-[var(--text-muted)]'}`}>
                    {scanText(r.detail)}{r.detail.location ? ` at ${r.detail.location}` : ''}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {chrono.length > 0 && (
        <div className="mt-4 border-t border-[var(--border-color)] pt-3">
          <button type="button" className="text-[12px] font-semibold text-accent hover:underline" onClick={() => setShowScans((v) => !v)}>
            {showScans ? 'Hide scan details' : `View scan details (${chrono.length})`}
          </button>
          {showScans && (
            <ol className="mt-2 flex flex-col gap-2">
              {chrono.map((sc, i) => (
                <li key={i} className="grid grid-cols-[132px_1fr] gap-3 text-[11px]">
                  <span className="whitespace-nowrap tabular-nums text-[var(--text-muted)]">{fmtWhen(sc.at)}</span>
                  <span>
                    <span className={`font-semibold ${scanStage(sc) === 'returning' || scanStage(sc) === 'returned' ? 'text-danger' : 'text-[var(--text-primary)]'}`}>{scanText(sc)}</span>
                    {sc.note && sc.status && sc.note !== sc.status && <span className="text-[var(--text-muted)]"> ({sc.status})</span>}
                    {sc.location && <span className="block text-[var(--text-muted)]">{sc.location}</span>}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

export function ShippingPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const period = usePeriod();
  const { range } = period;
  const shipQ = useQuery({ queryKey: ['shipping-rows', range?.from ?? null, range?.to ?? null], queryFn: () => listShippingRows({ from: range?.from, to: range?.to }) });
  const allRows = shipQ.data ?? [];
  const q = search.trim().toLowerCase();
  const rows = useMemo(
    () => (q
      ? allRows.filter((r) => [r.orderName, r.enqNo, r.grnNo, r.consignee, r.city, r.pin, r.phone, r.shipment?.awbNo].some((v) => (v ?? '').toLowerCase().includes(q)))
      : allRows),
    [allRows, q],
  );

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['shipping-rows'] });
    // Tracking can move a shipment into or out of NDR.
    qc.invalidateQueries({ queryKey: NDR_QUERY_KEY });
  };
  const [tab, setTab] = useState<'dispatches' | 'ndr'>('dispatches');
  const ndrCount = useQuery({ queryKey: NDR_QUERY_KEY, queryFn: getNdrQueue }).data?.rows.length ?? 0;
  const wrap = (fn: (id: number) => Promise<unknown>, okMsg: string) =>
    useMutation({ mutationFn: fn, onSuccess: () => { toast.push(okMsg, 'success'); refresh(); }, onError: (e) => toast.push(apiError(e, 'Failed'), 'error') });

  const [track, setTrack] = useState<TrackData | null>(null);

  const bookMut = wrap((id) => bookShipment(id), 'Courier booked — AWB generated');
  const pickupMut = wrap((id) => requestPickup(id), 'Pickup requested');
  const trackMut = useMutation({
    mutationFn: (id: number) => trackShipment(id),
    onSuccess: (s) => { setTrack({ ...s, scans: s.scans ?? [] }); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Tracking failed'), 'error'),
  });
  // Say what Delhivery actually recorded — a pre-pickup cancel stays in its "Ready to ship"
  // list until the next pickup run, and an unconfirmed one keeps being checked.
  const cancelMut = useMutation({
    mutationFn: (id: number) => cancelShipment(id),
    onSuccess: (s) => {
      const d = s.statusDetail ?? '';
      if (/not yet recorded/i.test(d)) toast.push(`Cancel sent, but Delhivery hasn't recorded it yet — the ERP keeps checking. ${s.awbNo ?? ''}`, 'warning');
      else toast.push(`Cancelled — Delhivery recorded it. It stays in Delhivery's "Ready to ship" until their next pickup run marks it Not Picked.`, 'success');
      refresh();
    },
    onError: (e) => toast.push(apiError(e, 'Failed'), 'error'),
  });
  // A COD order booked as Prepaid: switch it at Delhivery so the label and the delivery agent collect the cash.
  const confirmDlg = useConfirm();
  const codMut = useMutation({
    mutationFn: (id: number) => setShipmentCod(id),
    onSuccess: (s) => { toast.push(`AWB ${s.awbNo ?? ''} is now COD — collect ₹${formatINR(Number(s.codAmount ?? 0))}. Reprint the label.`, 'success'); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Delhivery did not accept the change'), 'error'),
  });
  const askCod = async (r: ShippingRow) => {
    const s = r.shipment, due = r.orderPayment?.codAmount;
    if (!s) return;
    if (!(await confirmDlg(`Change AWB ${s.awbNo} to Cash on Delivery? Order ${r.orderName ?? ''} is COD but was booked with Delhivery as Prepaid. Delhivery will collect ${due != null ? `₹${formatINR(due)}` : 'the amount due'} on delivery, and the label will show COD.`))) return;
    codMut.mutate(s.id);
  };
  const openLabel = (shipmentId: number) => window.open(`/print/shipping-label/${shipmentId}`, '_blank');

  const busy = bookMut.isPending || pickupMut.isPending || trackMut.isPending || cancelMut.isPending || codMut.isPending;

  return (
    <div>
      <SectionHeader
        title="Shipping — Delhivery"
        subtitle={tab === 'dispatches' && range ? `Dispatched ${range.label}` : undefined}
      />

      <div className="mb-2 inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1">
        <TabButton active={tab === 'dispatches'} onClick={() => setTab('dispatches')} label="Dispatches" />
        <TabButton active={tab === 'ndr'} onClick={() => setTab('ndr')} label={ndrCount ? `Failed deliveries (NDR) · ${ndrCount}` : 'Failed deliveries (NDR)'} />
      </div>

      {tab === 'ndr' ? <NdrQueue /> : (
      <>
      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full" title="Search by order, consignee, city, pincode, phone or AWB">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[260px]"
              aria-label="Search dispatches"
            />
          </div>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
      </FilterBar>

      <Table<ShippingRow>
        storageKey="shipping-delhivery"
        paginated
        title="Online Dispatches"
        count={q ? `${rows.length} of ${allRows.length} dispatch(es)` : `${rows.length} dispatch(es)`}
        rows={rows}
        rowKey={(r) => String(r.outwardGrnId)}
        loading={shipQ.isFetching && allRows.length === 0}
        empty={q ? 'No dispatches match this search.' : 'No online (Shopify) dispatch notes yet. Process a Shopify order through Packing → Dispatch Note first.'}
        columns={[
          { key: 'order', header: 'Order', minWidth: '120px', cell: (r) => <span className="font-extrabold text-accent">{r.orderName ?? r.enqNo ?? '—'}</span> },
          { key: 'consignee', header: 'Consignee', minWidth: '160px', cell: (r) => <span className="font-bold">{r.consignee ?? '—'}</span> },
          { key: 'location', header: 'Location', minWidth: '130px', cell: (r) => [r.city, r.pin].filter(Boolean).join(' · ') || '—' },
          { key: 'phone', header: 'Phone', minWidth: '110px', cell: (r) => r.phone ?? '—' },
          { key: 'boxes', header: 'Boxes', align: 'right', cell: (r) => r.boxCount },
          { key: 'wt', header: 'Wt (kg)', align: 'right', cell: (r) => r.weightKg.toFixed(2) },
          {
            key: 'awb', header: 'Tracking ID (AWB)', minWidth: '150px',
            cell: (r) => r.shipment?.awbNo
              ? (
                <a
                  href={`https://www.delhivery.com/track/package/${r.shipment.awbNo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs font-semibold text-accent hover:underline"
                  title="Track on Delhivery"
                >
                  {r.shipment.awbNo}
                </a>
              )
              : <span className="text-[var(--text-muted)]">—</span>,
          },
          {
            key: 'status', header: 'Status', minWidth: '130px',
            cell: (r) => !r.shipment
              ? <span className="text-[var(--text-muted)]">Not booked</span>
              : (
                <div className="flex items-center justify-center gap-1.5">
                  <Badge tone={STATUS_TONE[r.shipment.status] ?? 'muted'}>{STATUS_LABEL[r.shipment.status] ?? r.shipment.status}</Badge>
                  {r.shipment.statusDetail && (
                    <span className="text-[10px] text-[var(--text-muted)]">{r.shipment.statusDetail}{r.shipment.statusLocation ? ` · ${r.shipment.statusLocation}` : ''}</span>
                  )}
                  {r.shipment.status === 'Failed' && r.shipment.error && (
                    <span className="text-[10px] text-danger" title={r.shipment.error}>{r.shipment.error.slice(0, 40)}…</span>
                  )}
                </div>
              ),
          },
          {
            key: 'payment', header: 'Payment', minWidth: '130px', align: 'center',
            cell: (r) => {
              const s = r.shipment, op = r.orderPayment;
              const booked = s?.awbNo && !['Cancelled', 'Failed'].includes(s.status);
              // Not booked yet: how the order is paid (it is booked that way).
              if (!booked) return op ? <Badge tone={op.method === 'COD' ? 'accent' : 'muted'}>{op.method}</Badge> : <span className="text-[var(--text-muted)]">—</span>;
              if (s.paymentMode === 'COD') return <span title="Booked with Delhivery as Cash on Delivery"><Badge tone="accent">COD ₹{formatINR(Number(s.codAmount ?? 0))}</Badge></span>;
              if (op?.method !== 'COD') return <Badge tone="muted">Prepaid</Badge>;
              // COD order booked as Prepaid — the label says Prepaid and nothing is collected.
              const fixable = !['Delivered', 'RTO'].includes(s.status);
              return (
                <div className="flex flex-col items-center gap-1">
                  <span title="The order is Cash on Delivery but Delhivery has it as Prepaid"><Badge tone="danger">COD · booked Prepaid</Badge></span>
                  {fixable && <Button size="sm" variant="primary" disabled={busy} onClick={() => askCod(r)}>Update to COD</Button>}
                </div>
              );
            },
          },
          {
            key: 'actions', header: '', minWidth: '260px', align: 'right',
            cell: (r) => {
              const s = r.shipment;
              // Not yet booked, or a prior Cancelled/Failed attempt → offer (re-)booking.
              const rebookable = !s?.awbNo || ['Cancelled', 'Failed'].includes(s.status);
              if (rebookable) {
                return <Button size="sm" variant="primary" icon={<Truck size={12} />} disabled={busy} onClick={() => bookMut.mutate(r.outwardGrnId)}>{s?.awbNo ? 'Re-book' : 'Book Courier'}</Button>;
              }
              // Returned parcel: receive it back into stock (Sales Return against this dispatch).
              if (s.status === 'RTO') {
                return (
                  <div className="flex flex-wrap items-center justify-end gap-1">
                    <Button size="sm" variant="glass" icon={<RefreshCw size={12} />} disabled={busy} onClick={() => trackMut.mutate(s.id)}>Track</Button>
                    {r.returnNo
                      ? <Badge tone="success">Received · {r.returnNo}</Badge>
                      : (
                        <Button size="sm" variant="primary" icon={<Undo2 size={12} />} onClick={() => navigate(`/sales/sales-return?order=${encodeURIComponent(s.awbNo ?? '')}`)}>
                          Receive RTO
                        </Button>
                      )}
                  </div>
                );
              }
              return (
                <div className="flex flex-wrap justify-end gap-1">
                  <Button size="sm" variant="glass" icon={<Printer size={12} />} onClick={() => openLabel(s.id)}>Label</Button>
                  <Button size="sm" variant="glass" icon={<PackageCheck size={12} />} disabled={busy} onClick={() => pickupMut.mutate(s.id)}>Pickup</Button>
                  <Button size="sm" variant="glass" icon={<RefreshCw size={12} />} disabled={busy} onClick={() => trackMut.mutate(s.id)}>Track</Button>
                  {/* Cancellable only until Delhivery picks the parcel up. */}
                  {!['Delivered', 'Cancelled', 'RTO', 'PickedUp', 'InTransit', 'OutForDelivery', 'NDR'].includes(s.status) && (
                    <Button size="sm" variant="danger" icon={<XCircle size={12} />} disabled={busy} onClick={() => cancelMut.mutate(s.id)}>Cancel</Button>
                  )}
                </div>
              );
            },
          },
        ]}
      />
      </>
      )}

      <Modal
        open={!!track}
        onClose={() => setTrack(null)}
        title={`Tracking — AWB ${track?.awbNo ?? ''}`}
        size="md"
        footer={<Button variant="glass" onClick={() => setTrack(null)}>Close</Button>}
      >
        {track && <TrackingTimeline key={track.id} data={track} />}
      </Modal>
    </div>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
        (active
          ? 'bg-accent text-white shadow-sm'
          : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
      }
    >
      {label}
    </button>
  );
}
